import {
  doc,
  getDoc,
  setDoc,
  getDocs,
  collection,
  writeBatch,
} from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import { GuardData } from '../../manage_guard/service/guardService';
import { MonthlyAttendance, formatDate, getCurrentDateInfo } from './attendanceService';

export interface GuardSalaryInfo {
  guardId: string;
  guard: GuardData;
  month: string; // YYYY-MM format
  year: number;
  monthNumber: number;
  salaryTillToday: number;
  calculatedSalary: number;
  advanceSalary: number;
  currentBalance: number;
  previousBalance: number; // Previous unpaid balance from past cycles
  pendingMonths: number; // How many months are pending payment
  unpaidMonthKeys: string[]; // Array of month keys that are unpaid
}

export interface PaymentEntry {
  guardId: string;
  guardName: string;
  guardIdNumber?: string;
  amountToPay: number;
  deductionAmount?: number; // Amount deducted from balance
  advanceSalary: number;
  balanceBefore: number;
  balanceAfter: number;
  balanceReason?: string;
  months: string[]; // Month keys being paid
}

export interface SalaryDistributionSummary {
  totalGuards: number;
  totalAmount: number;
  totalAdvance: number;
  totalBalance: number;
  guards: GuardSalaryInfo[];
}

/**
 * Get salary distribution summary for ended cycles only (excludes current cycle)
 * Includes previous unpaid balances in gross salary
 */
export const getSalaryDistributionSummary = async (
  guards: GuardData[],
  companyId?: string
): Promise<SalaryDistributionSummary> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  // Get salary cycle configuration
  const { getSalaryCycleConfig, calculateCurrentCycle, checkAndUpdateCycle } = await import('./salaryCycleService');
  const cycleConfig = await getSalaryCycleConfig(uid);

  if (!cycleConfig) {
    throw new Error('Salary cycle configuration not found. Please set salary start date first.');
  }

  // Check and update cycle (moves previous cycle to history if new cycle started)
  const cycleInfo = await checkAndUpdateCycle(uid);
  const currentCycle = cycleInfo.currentCycle;

  // Get current date info
  const currentDate = getCurrentDateInfo();
  const currentCycleStartDate = currentCycle.startDate;

  // Process all guards in parallel
  const guardSalaryInfosResults = await Promise.all(
    guards.map(async (guard) => {
      try {
        // Get all months for this guard
        const monthsRef = collection(db, 'companies', uid, 'guardAttendance', guard.id, 'months');
        const monthsSnapshot = await getDocs(monthsRef);

        let totalSalary = 0;
        let advanceSalary = 0;
        let previousBalance = 0; // Accumulated unpaid balance from previous cycles
        let pendingMonths = 0;
        let latestBalance = 0;
        const unpaidMonthKeys: string[] = [];

        // Process each month document - only include cycles that have ended
        const monthDocs: { monthKey: string; data: MonthlyAttendance; cycleEndDate: Date }[] = [];

        monthsSnapshot.docs.forEach((doc) => {
          const data = doc.data() as MonthlyAttendance;

          // Determine the cycle end date for this month
          let cycleEndYear = data.year;
          let cycleEndMonth = data.month + 1;
          if (cycleEndMonth > 12) {
            cycleEndMonth = 1;
            cycleEndYear = data.year + 1;
          }

          // Calculate cycle end day (day before next cycle starts)
          let cycleEndDay = cycleConfig.salaryStartDate - 1;
          if (cycleEndDay < 1) {
            // If salary start date is 1, cycle ends on last day of current month
            cycleEndDay = new Date(data.year, data.month, 0).getDate();
            cycleEndMonth = data.month;
            cycleEndYear = data.year;
          }

          const cycleEndDate = new Date(cycleEndYear, cycleEndMonth - 1, cycleEndDay);
          cycleEndDate.setHours(23, 59, 59, 999);

          // Only include cycles that have ended (before current cycle start)
          if (cycleEndDate < currentCycleStartDate) {
            monthDocs.push({ monthKey: doc.id, data, cycleEndDate });
          }
        });

        // Sort by cycle end date (oldest first)
        monthDocs.sort((a, b) => a.cycleEndDate.getTime() - b.cycleEndDate.getTime());

        // Separate into previous cycles and most recent ended cycle
        if (monthDocs.length > 0) {
          const mostRecentCycleEndDate = monthDocs[monthDocs.length - 1].cycleEndDate;
          const previousCycles = monthDocs.filter(m => m.cycleEndDate < mostRecentCycleEndDate);
          const mostRecentCycle = monthDocs.filter(m => m.cycleEndDate.getTime() === mostRecentCycleEndDate.getTime());

          // Calculate previous balance
          for (const { data } of previousCycles) {
            if (data.paymentStatus !== 'paid') {
              const salary = data.salaryTillToday || data.calculatedSalary || 0;
              const advance = data.advanceSalary || 0;
              const balance = data.balance !== undefined
                ? data.balance
                : (salary - advance);
              previousBalance += balance;
            } else if (data.balance !== undefined && data.balance > 0) {
              previousBalance += data.balance;
            }
          }

          // Calculate salary for the most recent ended cycle
          for (const { data, monthKey } of mostRecentCycle) {
            if (data.paymentStatus !== 'paid') {
              const salary = data.salaryTillToday || data.calculatedSalary || 0;
              totalSalary += salary;
              advanceSalary += data.advanceSalary || 0;
              pendingMonths++;
              unpaidMonthKeys.push(monthKey);

              if (data.balance !== undefined) {
                latestBalance = data.balance;
              } else {
                latestBalance = salary - (data.advanceSalary || 0);
              }
            } else if (data.balance !== undefined && data.balance > 0) {
              const salary = data.salaryTillToday || data.calculatedSalary || 0;
              totalSalary += salary;
              advanceSalary += data.advanceSalary || 0;
              latestBalance = data.balance;
              pendingMonths++;
              unpaidMonthKeys.push(monthKey);
            }
          }

          // Add previous balance to gross salary
          if (previousBalance > 0) {
            totalSalary += previousBalance;
          }

          // Final balance
          const finalBalance = totalSalary - advanceSalary;

          if (pendingMonths > 0 || previousBalance > 0) {
            return {
              guardId: guard.id,
              guard,
              month: mostRecentCycle[0]?.monthKey || monthDocs[0]?.monthKey || '',
              year: mostRecentCycle[0]?.data.year || monthDocs[0]?.data.year || currentDate.year,
              monthNumber: mostRecentCycle[0]?.data.month || monthDocs[0]?.data.month || currentDate.month,
              salaryTillToday: totalSalary,
              calculatedSalary: totalSalary,
              advanceSalary,
              currentBalance: finalBalance,
              previousBalance,
              pendingMonths,
              unpaidMonthKeys,
            };
          }
        }
      } catch (error) {
        console.error(`Error getting salary info for guard ${guard.id}:`, error);
      }
      return null;
    })
  );

  const guardSalaryInfos = guardSalaryInfosResults.filter((info): info is GuardSalaryInfo => info !== null);

  const totalAmount = guardSalaryInfos.reduce((sum, g) => sum + g.salaryTillToday, 0);
  const totalAdvance = guardSalaryInfos.reduce((sum, g) => sum + g.advanceSalary, 0);
  const totalBalance = guardSalaryInfos.reduce((sum, g) => sum + g.currentBalance, 0);

  return {
    totalGuards: guardSalaryInfos.length,
    totalAmount,
    totalAdvance,
    totalBalance,
    guards: guardSalaryInfos,
  };
};

export interface GuardSummary {
  guardId: string;
  totalPaidSalaryTillNow: number;
  totalAdvanceTaken: number;
  totalDeductions: number;
  totalPresents: number;
  totalAbsents: number;
  totalLeaves: number;
  deductions: Array<{
    month: string;
    year: number;
    monthNumber: number;
    amount: number;
    reason?: string;
    paidAt: Date;
  }>;
  payments: Array<{
    month: string;
    year: number;
    monthNumber: number;
    amount: number;
    paidAt: Date;
  }>;
  updatedAt: Date;
}

/**
 * Process salary rollout - mark months as paid and update balances
 */
export const processSalaryRollout = async (
  payments: PaymentEntry[],
  companyId?: string
): Promise<void> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  const batch = writeBatch(db);
  const currentDate = new Date();

  // Track guard summaries to update
  const guardSummaries = new Map<string, GuardSummary>();

  for (const payment of payments) {
    // Get all unpaid months for this guard
    const monthsRef = collection(db, 'companies', uid, 'guardAttendance', payment.guardId, 'months');
    const monthsSnapshot = await getDocs(monthsRef);

    // Initialize guard summary if not exists
    if (!guardSummaries.has(payment.guardId)) {
      const summaryRef = doc(db, 'companies', uid, 'guardAttendance', payment.guardId);
      const summaryDoc = await getDoc(summaryRef);

      if (summaryDoc.exists()) {
        guardSummaries.set(payment.guardId, summaryDoc.data() as GuardSummary);
      } else {
        guardSummaries.set(payment.guardId, {
          guardId: payment.guardId,
          totalPaidSalaryTillNow: 0,
          totalAdvanceTaken: 0,
          totalDeductions: 0,
          totalPresents: 0,
          totalAbsents: 0,
          totalLeaves: 0,
          deductions: [],
          payments: [],
          updatedAt: currentDate,
        });
      }
    }

    const guardSummary = guardSummaries.get(payment.guardId)!;

    // First, calculate total unpaid salary for the months being paid
    const monthsToPay = payment.months.length > 0
      ? monthsSnapshot.docs.filter(d => payment.months.includes(d.id) && d.data().paymentStatus !== 'paid')
      : monthsSnapshot.docs.filter(d => d.data().paymentStatus !== 'paid');

    const totalUnpaidSalary = monthsToPay.reduce((sum, monthDoc) => {
      const data = monthDoc.data() as MonthlyAttendance;
      return sum + (data.salaryTillToday || data.calculatedSalary || 0);
    }, 0);

    // Process each unpaid month - mark ALL unpaid months as paid
    for (const monthDoc of monthsToPay) {
      const data = monthDoc.data() as MonthlyAttendance;
      const docRef = monthDoc.ref;

      // Calculate amounts proportionally based on month salary
      const monthSalary = data.salaryTillToday || data.calculatedSalary || 0;
      const deductionForThisMonth = totalUnpaidSalary > 0 && payment.deductionAmount
        ? Math.round((monthSalary / totalUnpaidSalary) * payment.deductionAmount)
        : 0;

      const paidForThisMonth = totalUnpaidSalary > 0
        ? Math.round((monthSalary / totalUnpaidSalary) * payment.amountToPay)
        : monthSalary;

      const updatedData: any = {
        ...data,
        paymentStatus: 'paid',
        paidAt: currentDate,
        paidAmount: paidForThisMonth,
        balance: payment.balanceAfter,
        lastPaymentDate: currentDate,
      };

      // Only include deductionAmount if it exists
      if (deductionForThisMonth > 0) {
        updatedData.deductionAmount = deductionForThisMonth;
      }

      // Only include balanceReason if it exists and is not empty
      if (payment.balanceReason && payment.balanceReason.trim() !== '') {
        updatedData.balanceReason = payment.balanceReason;
      }

      batch.set(docRef, updatedData);

      // Update guard summary
      guardSummary.totalPaidSalaryTillNow += paidForThisMonth;
      guardSummary.totalAdvanceTaken += (data.advanceSalary || 0);
      guardSummary.totalPresents += (data.presentDays || 0);
      guardSummary.totalAbsents += (data.absentDays || 0);
      guardSummary.totalLeaves += (data.leaveDays || 0);

      if (deductionForThisMonth > 0) {
        guardSummary.totalDeductions += deductionForThisMonth;
        guardSummary.deductions.push({
          month: monthDoc.id,
          year: data.year,
          monthNumber: data.month,
          amount: deductionForThisMonth,
          reason: payment.balanceReason || undefined,
          paidAt: currentDate,
        });
      }

      guardSummary.payments.push({
        month: monthDoc.id,
        year: data.year,
        monthNumber: data.month,
        amount: paidForThisMonth,
        paidAt: currentDate,
      });
    }

    guardSummary.updatedAt = currentDate;
  }

  // Update guard summaries
  guardSummaries.forEach((summary, guardId) => {
    const summaryRef = doc(db, 'companies', uid, 'guardAttendance', guardId);
    batch.set(summaryRef, summary, { merge: true });
  });

  await batch.commit();

  // Save to general ledger as expense
  try {
    const totalSalaryPaid = payments.reduce((sum, p) => sum + p.amountToPay, 0);
    if (totalSalaryPaid > 0) {
      const { saveSalaryExpense } = await import('../../finance/service/ledgerService');
      const reference = `salary_rollout_${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}_${Date.now()}`;
      const description = `Salary Payment - ${payments.length} guard(s)`;
      await saveSalaryExpense(totalSalaryPaid, description, currentDate, reference, companyId);
    }
  } catch (error) {
    console.error('Error saving salary expense to ledger:', error);
    // Don't throw - ledger entry failure shouldn't fail the rollout
  }
};

export interface MonthlySalarySummary {
  guardId: string;
  guard: GuardData;
  month: string; // YYYY-MM format
  year: number;
  monthNumber: number;
  salary: number; // salaryTillToday or calculatedSalary
  advanceSalary: number;
  paidAmount?: number;
  deductionAmount?: number;
  balance?: number;
  balanceReason?: string;
  paymentStatus: 'paid' | 'unpaid' | 'current';
  paidAt?: Date;
}

/**
 * Get available paid months/years for salary summary
 */
export interface AvailableMonth {
  year: number;
  month: number;
  monthKey: string;
}

export const getAvailablePaidMonths = async (
  companyId?: string
): Promise<AvailableMonth[]> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  const currentDate = getCurrentDateInfo();
  const monthSet = new Set<string>();
  const availableMonths: AvailableMonth[] = [];

  // Get all guards
  const { loadAllGuardsForExport } = await import('../../manage_guard/service/guardService');
  const guards = await loadAllGuardsForExport(companyId);

  for (const guard of guards) {
    try {
      const monthsRef = collection(db, 'companies', uid, 'guardAttendance', guard.id, 'months');
      const monthsSnapshot = await getDocs(monthsRef);

      for (const monthDoc of monthsSnapshot.docs) {
        const data = monthDoc.data() as MonthlyAttendance;

        // Only include paid months
        if (data.paymentStatus === 'paid') {
          const monthKey = monthDoc.id;

          // Don't include future months
          if (data.year > currentDate.year ||
            (data.year === currentDate.year && data.month > currentDate.month)) {
            continue;
          }

          // Don't include if already added
          if (!monthSet.has(monthKey)) {
            monthSet.add(monthKey);
            availableMonths.push({
              year: data.year,
              month: data.month,
              monthKey,
            });
          }
        }
      }
    } catch (error) {
      console.error(`Error getting available months for guard ${guard.id}:`, error);
    }
  }

  // Sort by year and month (newest first)
  availableMonths.sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  });

  return availableMonths;
};

/**
 * Get salary summary for all guards for a specific month/year (only paid data)
 */
export const getMonthlySalarySummary = async (
  year: number,
  month: number,
  companyId?: string
): Promise<MonthlySalarySummary[]> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  const monthKey = `${year}-${String(month).padStart(2, '0')}`;
  const summary: MonthlySalarySummary[] = [];

  // Get all guards
  const { loadAllGuardsForExport } = await import('../../manage_guard/service/guardService');
  const guards = await loadAllGuardsForExport(companyId);

  for (const guard of guards) {
    try {
      const monthDocRef = doc(db, 'companies', uid, 'guardAttendance', guard.id, 'months', monthKey);
      const monthDoc = await getDoc(monthDocRef);

      if (monthDoc.exists()) {
        const data = monthDoc.data() as MonthlyAttendance;

        // Only include paid data
        if (data.paymentStatus === 'paid') {
          summary.push({
            guardId: guard.id,
            guard,
            month: monthKey,
            year: data.year,
            monthNumber: data.month,
            salary: data.salaryTillToday || data.calculatedSalary || 0,
            advanceSalary: data.advanceSalary || 0,
            paidAmount: data.paidAmount,
            deductionAmount: data.deductionAmount,
            balance: data.balance,
            balanceReason: data.balanceReason,
            paymentStatus: data.paymentStatus,
            paidAt: data.paidAt,
          });
        }
      }
    } catch (error) {
      console.error(`Error getting salary summary for guard ${guard.id}:`, error);
    }
  }

  return summary;
};

/**
 * Get individual guard summary from guardAttendance collection
 */
export const getGuardSummary = async (
  guardId: string,
  companyId?: string
): Promise<GuardSummary | null> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  const summaryRef = doc(db, 'companies', uid, 'guardAttendance', guardId);
  const summaryDoc = await getDoc(summaryRef);

  if (!summaryDoc.exists()) {
    return null;
  }

  const data = summaryDoc.data();
  return {
    guardId: data.guardId || guardId,
    totalPaidSalaryTillNow: data.totalPaidSalaryTillNow || 0,
    totalAdvanceTaken: data.totalAdvanceTaken || 0,
    totalDeductions: data.totalDeductions || 0,
    totalPresents: data.totalPresents || 0,
    totalAbsents: data.totalAbsents || 0,
    totalLeaves: data.totalLeaves || 0,
    deductions: (data.deductions || []).map((d: any) => ({
      ...d,
      paidAt: d.paidAt?.toDate ? d.paidAt.toDate() : new Date(d.paidAt),
    })),
    payments: (data.payments || []).map((p: any) => ({
      ...p,
      paidAt: p.paidAt?.toDate ? p.paidAt.toDate() : new Date(p.paidAt),
    })),
    updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt),
  };
};

